import {
  Button,
  Card,
  CardContent,
  Col,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Row,
  type FormInstance,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect } from "react";

export type RawRetailerAssociationFormMode = "create" | "edit";

export interface RawRetailerAssociationFormValues {
  rawRetailerName: string;
}

interface MasterDataRawRetailerAssociationAddEditFormProps {
  form: FormInstance<RawRetailerAssociationFormValues>;
  mode: RawRetailerAssociationFormMode;
  initialValues: RawRetailerAssociationFormValues;
  lockedRetailerName: string;
  isSaving: boolean;
  isFormSubmittable: boolean;
  onSubmit: (values: RawRetailerAssociationFormValues) => Promise<void> | void;
  onCancel: () => void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const EMPTY_RAW_ASSOC_FORM_VALUES: RawRetailerAssociationFormValues = {
  rawRetailerName: "",
};

export const MasterDataRawRetailerAssociationAddEditForm = ({
  form,
  mode,
  initialValues,
  lockedRetailerName,
  isSaving,
  isFormSubmittable,
  onSubmit,
  onCancel,
  onFormValidityChange,
}: MasterDataRawRetailerAssociationAddEditFormProps) => {
  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const rawRetailerName = values.rawRetailerName?.trim() ?? "";
    const hasRequiredValues = rawRetailerName !== "";
    const isDirty =
      mode === "create"
        ? true
        : rawRetailerName !== (initialValues.rawRetailerName?.trim() ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  return (
    <ErrorBoundary>
      <Card className="bg-white shadow-sm border-1 border-color-grey-25 rounded-medium mb-2">
        <CardContent className="p-3">
          <Form<RawRetailerAssociationFormValues>
            form={form}
            onFinish={onSubmit}
            onFieldsChange={updateFormValidity}
            className="master-data-dc-add-edit"
          >
            <Flex align="center" justifyContent="space-between" gap={1}>
              <Row style={{ flex: 1 }}>
                <Col span={4}>
                  <Form.Item>
                    <Input
                      value={lockedRetailerName}
                      isDisabled
                      aria-label="Associated retailer"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="rawRetailerName"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {
                        required: true,
                        message: "Raw retailer name is required",
                      },
                    ]}
                  >
                    <Input placeholder="Raw retailer name" />
                  </Form.Item>
                </Col>
              </Row>
              <Flex align="center" gap={0.5}>
                <Button
                  size="small"
                  color="primary"
                  variant="clear"
                  onPress={() => form.submit()}
                  isLoading={isSaving}
                  isDisabled={isSaving || !isFormSubmittable}
                  aria-label="Save raw retailer association"
                >
                  <Icon name={Icons.Check} />
                </Button>
                <Button
                  size="small"
                  color="grey"
                  variant="clear"
                  onPress={onCancel}
                  isDisabled={isSaving}
                  aria-label="Cancel"
                >
                  <Icon name={Icons.Close} />
                </Button>
              </Flex>
            </Flex>
          </Form>
        </CardContent>
      </Card>
    </ErrorBoundary>
  );
};
